import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "att_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
};

const statusBadge = (s) => {
  const map = {
    pending_confirmation: { bg: "#fef3c7", fg: "#92400e", label: "PENDING" },
    confirmed: { bg: "#d1fae5", fg: "#065f46", label: "CONFIRMED" },
    payment_link_sent: { bg: "#dbeafe", fg: "#1e40af", label: "PAYMENT SENT" },
    paid: { bg: "#d1fae5", fg: "#065f46", label: "PAID" },
    declined: { bg: "#fee2e2", fg: "#991b1b", label: "DECLINED" },
    quote_requested: { bg: "#e5e7eb", fg: "#374151", label: "QUOTE" },
  };
  const c = map[s] || { bg: "#e5e7eb", fg: "#374151", label: s?.toUpperCase() || "—" };
  return (
    <span style={{
      background: c.bg, color: c.fg, padding: "3px 10px",
      borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
    }}>{c.label}</span>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [inputToken, setInputToken] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/bookings`, {
        headers: { "X-Admin-Token": token },
      });
      setBookings(r.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Λάθος admin token. Δοκίμασε ξανά.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError(`Σφάλμα φόρτωσης: ${e.response?.status || e.message}`);
      }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const login = (e) => {
    e.preventDefault();
    const t = inputToken.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setBookings([]);
  };

  const act = async (b, action) => {
    const verb = action === "confirm" ? "CONFIRM" : "DECLINE";
    if (!window.confirm(`${verb} booking ${b.reference}?\n\n${b.name} — ${b.pickup} → ${b.dropoff}\n${b.date} at ${b.time}`)) return;
    setBusyId(b.id);
    try {
      const r = await axios.post(
        `${API}/admin/bookings/${b.id}/action`,
        null,
        {
          params: { action },
          headers: { "X-Admin-Token": token },
        }
      );
      // Response is HTML — we don't render it, just refresh the list.
      if (r.status === 200) {
        await load();
      }
    } catch (e) {
      alert(`Αποτυχία ${verb}: ${e.response?.status || e.message}`);
    } finally { setBusyId(""); }
  };

  // Login screen
  if (!token) {
    return (
      <div style={{
        minHeight: "100vh", background: "#0a1830", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
        <form onSubmit={login} style={{
          background: "#0f2544", padding: 32, borderRadius: 16, width: "100%", maxWidth: 380,
          border: "1px solid rgba(230,183,92,0.3)",
        }} data-testid="admin-login-form">
          <h1 style={{ margin: "0 0 6px", fontSize: 22, color: "#e6b75c" }}>Admin Dashboard</h1>
          <p style={{ margin: "0 0 20px", fontSize: 13, opacity: 0.7 }}>
            Athens Airport Taxi Transfers
          </p>
          <label style={{ display: "block", fontSize: 11, letterSpacing: 1.5, opacity: 0.7, marginBottom: 6, textTransform: "uppercase" }}>
            Admin Token
          </label>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            data-testid="admin-token-input"
            placeholder="Paste your token"
            autoFocus
            style={{
              width: "100%", padding: "12px 14px", borderRadius: 10,
              background: "#0a1830", border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff", fontSize: 14, marginBottom: 16, boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 10,
              background: "#e6b75c", color: "#0a1830", fontWeight: 700,
              border: "none", cursor: "pointer", fontSize: 14, letterSpacing: 0.5,
            }}
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const filtered = bookings.filter((b) => {
    if (filter === "pending") return b.status === "pending_confirmation";
    if (filter === "confirmed") return ["confirmed", "payment_link_sent", "paid"].includes(b.status);
    if (filter === "declined") return b.status === "declined";
    return true;
  });

  return (
    <div style={{ minHeight: "100vh", background: "#0a1830", color: "#fff", padding: "24px 16px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, color: "#e6b75c" }}>Bookings</h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, opacity: 0.6 }}>Athens Airport Taxi Transfers · Admin</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-btn"
              style={{
                padding: "8px 14px", borderRadius: 8, background: "transparent",
                border: "1px solid rgba(230,183,92,0.5)", color: "#e6b75c",
                cursor: "pointer", fontSize: 13, fontWeight: 600,
              }}
            >{loading ? "Loading…" : "↻ Refresh"}</button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              style={{
                padding: "8px 14px", borderRadius: 8, background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)", color: "#fff",
                cursor: "pointer", fontSize: 13,
              }}
            >Logout</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { k: "pending", label: `Pending (${bookings.filter(b => b.status === "pending_confirmation").length})` },
            { k: "confirmed", label: `Confirmed (${bookings.filter(b => ["confirmed","payment_link_sent","paid"].includes(b.status)).length})` },
            { k: "declined", label: `Declined (${bookings.filter(b => b.status === "declined").length})` },
            { k: "all", label: `All (${bookings.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`filter-${t.k}`}
              style={{
                padding: "8px 14px", borderRadius: 999,
                background: filter === t.k ? "#e6b75c" : "transparent",
                color: filter === t.k ? "#0a1830" : "#fff",
                border: `1px solid ${filter === t.k ? "#e6b75c" : "rgba(255,255,255,0.15)"}`,
                cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
              }}
            >{t.label}</button>
          ))}
        </div>

        {error && (
          <div style={{
            padding: 12, borderRadius: 10, background: "rgba(220,38,38,0.15)",
            border: "1px solid rgba(220,38,38,0.3)", color: "#fecaca",
            marginBottom: 16, fontSize: 13,
          }}>{error}</div>
        )}

        {filtered.length === 0 && !loading && (
          <div style={{
            padding: 40, textAlign: "center", opacity: 0.5,
            background: "#0f2544", borderRadius: 12, fontSize: 14,
          }} data-testid="admin-empty-state">Δεν υπάρχουν κρατήσεις σε αυτήν την κατηγορία.</div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {filtered.map((b) => {
            const canAct = b.status === "pending_confirmation";
            return (
              <div key={b.id} data-testid={`booking-card-${b.reference}`} style={{
                background: "#0f2544", borderRadius: 14, padding: 18,
                border: "1px solid rgba(230,183,92,0.12)",
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.5, textTransform: "uppercase" }}>{b.reference}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{b.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                      <a href={`mailto:${b.email}`} style={{ color: "#e6b75c" }}>{b.email}</a>
                      {" · "}
                      <a href={`tel:${b.phone}`} style={{ color: "#e6b75c" }}>{b.phone}</a>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {statusBadge(b.status)}
                    <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>Received {fmtDate(b.created_at)}</div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, padding: "12px 0", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, opacity: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Pickup</div>
                    <div style={{ fontSize: 13 }}>{b.date} · {b.time}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{b.pickup}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, opacity: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Dropoff</div>
                    <div style={{ fontSize: 13 }}>{b.service}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{b.dropoff || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, opacity: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Passengers / Luggage</div>
                    <div style={{ fontSize: 13 }}>{b.passengers} pax · {b.luggage || 0} bags</div>
                    {b.flight && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Flight: {b.flight}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, opacity: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Payment</div>
                    <div style={{ fontSize: 13 }}>Card online (Stripe)</div>
                    {b.estimated_amount && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>€{b.estimated_amount}</div>}
                  </div>
                </div>

                {b.notes && (
                  <div style={{ padding: "10px 12px", background: "rgba(230,183,92,0.08)", borderRadius: 8, fontSize: 12, marginTop: 12 }}>
                    <span style={{ opacity: 0.6 }}>Notes: </span>{b.notes}
                  </div>
                )}

                {canAct && (
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button
                      onClick={() => act(b, "confirm")}
                      disabled={busyId === b.id}
                      data-testid={`confirm-${b.reference}`}
                      style={{
                        flex: 1, padding: "12px 16px", borderRadius: 10,
                        background: "#059669", color: "#fff", fontWeight: 700,
                        border: "none", cursor: busyId === b.id ? "wait" : "pointer",
                        fontSize: 14, opacity: busyId === b.id ? 0.6 : 1,
                      }}
                    >{busyId === b.id ? "…" : "✓ Confirm"}</button>
                    <button
                      onClick={() => act(b, "decline")}
                      disabled={busyId === b.id}
                      data-testid={`decline-${b.reference}`}
                      style={{
                        flex: 1, padding: "12px 16px", borderRadius: 10,
                        background: "#dc2626", color: "#fff", fontWeight: 700,
                        border: "none", cursor: busyId === b.id ? "wait" : "pointer",
                        fontSize: 14, opacity: busyId === b.id ? 0.6 : 1,
                      }}
                    >{busyId === b.id ? "…" : "✕ Decline"}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center", opacity: 0.4, fontSize: 11, marginTop: 30 }}>
          Athens Airport Taxi Transfers · Admin
        </div>
      </div>
    </div>
  );
}
